import { BoldButton } from '@components/core-ui/form-elements/buttons/Button/variations/BoldButton';
import { JOB_STATUSES_ENUM } from 'app/types/enums/jobStatuses';
import { jobCanBeMarkedAsComplete } from '@helpers/jobStatus/jobCanBeMarkedAsComplete';
import { jobCanBeMarkedAsEdited } from '@helpers/jobStatus/jobCanBeMarkedAsEdited';
import { jobCanBeMarkedAsProofread } from '@helpers/jobStatus/jobCanBeMarkedAsProofread';
import { jobIsReadyForFinal } from '@helpers/jobStatus/jobIsReadyForFinal';
import { jobIsReadyForProofReading } from '@helpers/jobStatus/jobIsReadyForProofReading';
import { jobIsReadyForTranscribing } from '@helpers/jobStatus/jobIsReadyForTranscribing';
import { useAuthContext } from '@providers/AuthProvider';
import { JobResponseDto, MeResponseDtoOrganization } from 'app/API';
import { useJobStatusActions } from 'app/hooks/useJobStatusActions';

export const showClaimButton = (job?: JobResponseDto, organization?: MeResponseDtoOrganization) =>
  job &&
  !job.assignee &&
  !job.asOneLocked &&
  ((organization?.permissions?.editJobTranscript &&
    jobIsReadyForTranscribing((job?.status ?? '') as keyof typeof JOB_STATUSES_ENUM)) ||
    (organization?.permissions?.proofreadJobTranscript &&
      jobIsReadyForProofReading((job?.status ?? '') as keyof typeof JOB_STATUSES_ENUM)) ||
    (organization?.permissions?.finalizeJobTranscript &&
      jobIsReadyForFinal((job?.status ?? '') as keyof typeof JOB_STATUSES_ENUM)));

export const showMarkAsCompleteButton = (job?: JobResponseDto, organization?: MeResponseDtoOrganization) =>
  job &&
  organization?.permissions?.markJobComplete &&
  jobCanBeMarkedAsComplete((job?.status ?? '') as keyof typeof JOB_STATUSES_ENUM);

export const showMarkAsEditedButton = (job?: JobResponseDto, organization?: MeResponseDtoOrganization) =>
  job &&
  organization?.permissions?.editJobTranscript &&
  jobCanBeMarkedAsEdited((job?.status ?? '') as keyof typeof JOB_STATUSES_ENUM);

export const showMarkAsProofreadButton = (job?: JobResponseDto, organization?: MeResponseDtoOrganization) =>
  job &&
  organization?.permissions?.proofreadJobTranscript &&
  jobCanBeMarkedAsProofread((job?.status ?? '') as keyof typeof JOB_STATUSES_ENUM);

type TProps = {
  job: JobResponseDto;
  reload: () => Promise<void>;
};

export const JobStatusActionsButton = ({ job, reload }: TProps) => {
  const { workspace, organization } = useAuthContext();
  const { claim, markAsComplete, markAsEdited, markAsProofread, isPending } = useJobStatusActions({
    workspaceId: workspace?.id || '',
    onSuccess: reload,
  });

  if (showClaimButton(job, organization)) {
    return (
      <div className="w-[200px] mr-4">
        <BoldButton className="w-[200px]" disabled={isPending} onClick={() => claim(job.id)} id="claim_job_button">
          Claim Job
        </BoldButton>
      </div>
    );
  }

  if (showMarkAsCompleteButton(job, organization)) {
    return (
      <BoldButton disabled={isPending} onClick={() => markAsComplete(job.id)} id="mark_as_complete">
        Mark As Complete
      </BoldButton>
    );
  }

  if (showMarkAsEditedButton(job, organization)) {
    return (
      <BoldButton disabled={isPending} onClick={() => markAsEdited(job.id)} id="mark_as_edited_button">
        Mark as Edited
      </BoldButton>
    );
  }

  if (showMarkAsProofreadButton(job, organization)) {
    return (
      <BoldButton disabled={isPending} onClick={() => markAsProofread(job.id)} id="mark_as_proofread">
        Mark as Proofread
      </BoldButton>
    );
  }

  return null;
};
